import React, { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import { Users, Plus, Trash2, Check } from "lucide-react";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const FamilyMembershipPage = () => {
  const [formData, setFormData] = useState({
    family_name: "",
    phone: "",
    members: [{ first_name: "", last_name: "", age_group: "mini" }]
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const addMember = () => {
    setFormData({
      ...formData,
      members: [...formData.members, { first_name: "", last_name: "", age_group: "mini" }]
    });
  };

  const removeMember = (index) => {
    if (formData.members.length === 1) return;
    const updated = formData.members.filter((_, i) => i !== index);
    setFormData({ ...formData, members: updated });
  };

  const updateMember = (index, field, value) => {
    const updated = [...formData.members];
    updated[index][field] = value;
    setFormData({ ...formData, members: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/members/family`, {
        ...formData,
        fee: 50.0
      });
      toast.success("Family membership created!");
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create membership");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6" data-testid="family-success">
        <Card className="border-status-success/30 bg-status-success/5">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-status-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-status-success" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Family Membership Created!
            </h2>
            <p className="text-text-secondary mb-2">
              The {formData.family_name} family is now registered.
            </p>
            <p className="text-text-muted text-sm mb-6">
              {formData.members.length} family member{formData.members.length > 1 ? 's' : ''} added
            </p>
            <Button
              onClick={() => {
                setSuccess(false);
                setFormData({
                  family_name: "",
                  phone: "",
                  members: [{ first_name: "", last_name: "", age_group: "mini" }]
                });
              }}
              className="bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Create Another
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="family-membership-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
          <Users className="h-6 w-6 text-accent" />
          Family Membership
        </h1>
        <p className="text-text-secondary mt-1">$50 for the whole family</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="family-form">
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Family Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Family Name *</Label>
              <Input
                value={formData.family_name}
                onChange={(e) => setFormData({ ...formData, family_name: e.target.value })}
                placeholder="e.g., Smith Family"
                required
                data-testid="fam-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Optional"
                data-testid="fam-phone"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary/20">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Family Members</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={addMember}>
              <Plus className="h-4 w-4 mr-1" /> Add Member
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.members.map((member, idx) => (
              <div key={idx} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-text-primary">Member {idx + 1}</span>
                  {formData.members.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-status-error"
                      onClick={() => removeMember(idx)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">First Name *</Label>
                    <Input
                      value={member.first_name}
                      onChange={(e) => updateMember(idx, "first_name", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Last Name *</Label>
                    <Input
                      value={member.last_name}
                      onChange={(e) => updateMember(idx, "last_name", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Age Group *</Label>
                  <Select
                    value={member.age_group}
                    onValueChange={(value) => updateMember(idx, "age_group", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_GROUPS.map((ag) => (
                        <SelectItem key={ag} value={ag} className="capitalize">
                          {ag.charAt(0).toUpperCase() + ag.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-accent/30 bg-accent/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="font-heading font-bold text-text-primary">Total</span>
                <p className="text-xs text-text-muted">{formData.members.length} members</p>
              </div>
              <span className="font-heading font-bold text-2xl text-accent">$50.00</span>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-accent text-white hover:bg-accent/90 font-bold h-12"
              data-testid="fam-submit"
            >
              {submitting ? "Processing..." : "Create Family Membership ($50)"}
            </Button>
          </CardContent>
        </Card>
      </form>

      {/* Benefits */}
      <Card className="border-primary/20 bg-secondary/30">
        <CardContent className="p-4">
          <h3 className="font-heading font-bold text-text-primary mb-2">Family Benefits</h3>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• All family members earn points</li>
            <li>• Unlimited family members included</li>
            <li>• Save compared to individual memberships</li>
            <li>• Support your local play day organization</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyMembershipPage;
